import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import { usePlayer, type PlayableChapter } from '@/lib/PlayerContext';
import { useAuth } from '@/lib/auth/AuthContext';
import { ShareButton } from './ShareButton';
import type { GlobalPlayerRecordListenProgressMutation } from '@/__generated__/GlobalPlayerRecordListenProgressMutation.graphql';

/**
 * Mutation to record listen progress
 */
const recordListenProgressMutation = graphql`
  mutation GlobalPlayerRecordListenProgressMutation($input: RecordListenProgressInput!) {
    recordListenProgress(input: $input) {
      listenProgress {
        progressSeconds
        durationSeconds
        completed
        listenedAt
      }
      userErrors {
        message
        field
      }
    }
  }
`;

export function GlobalPlayer() {
  const { currentEpisode, closePlayer } = usePlayer();

  if (!currentEpisode) {
    return null;
  }

  return <PlayerUI onClose={closePlayer} />;
}

interface PlayerUIProps {
  onClose: () => void;
}

function PlayerUI({ onClose }: PlayerUIProps) {
  const { currentEpisode, isPlaying, setIsPlaying, onEpisodeEnded } = usePlayer();
  const { isAuthenticated } = useAuth();
  const audioRef = useRef<HTMLAudioElement>(null);
  const speedMenuRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLButtonElement>(null);
  const lastProgressSyncRef = useRef<number>(0);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(() => {
    const saved = localStorage.getItem('playbackSpeed');
    return saved ? Number(saved) : 1;
  });

  // Mutation for recording listen progress
  const [commitRecordProgress] = useMutation<GlobalPlayerRecordListenProgressMutation>(recordListenProgressMutation);

  // Function to sync listen progress to the server
  const syncListenProgress = useCallback((progressSeconds: number, durationSeconds: number, force = false) => {
    if (!isAuthenticated || !currentEpisode) return;

    // Throttle: only sync every 15 seconds unless forced
    const now = Date.now();
    if (!force && now - lastProgressSyncRef.current < 15000) return;
    lastProgressSyncRef.current = now;

    commitRecordProgress({
      variables: {
        input: {
          topicSlug: currentEpisode.topicSlug,
          episodeId: currentEpisode.episodeId,
          progressSeconds: Math.floor(progressSeconds),
          durationSeconds: Math.floor(durationSeconds),
        },
      },
      onError: (error) => {
        console.error('Failed to record listen progress:', error);
      },
    });
  }, [isAuthenticated, currentEpisode, commitRecordProgress]);

  // Guard against null episode (shouldn't happen due to parent check)
  if (!currentEpisode) return null;

  const chapters: PlayableChapter[] = currentEpisode.chapters || [];
  const hasChapters = chapters.length > 0;

  // Find current chapter based on playback time
  const currentChapter = useMemo(() => {
    if (!hasChapters) return null;
    const currentTimeMs = currentTime * 1000;
    return chapters.find(
      (ch) => currentTimeMs >= ch.startTimeMs && currentTimeMs < ch.endTimeMs
    ) || null;
  }, [chapters, currentTime, hasChapters]);

  // Find current chapter index for navigation
  const currentChapterIndex = useMemo(() => {
    if (!hasChapters || !currentChapter) return -1;
    return chapters.findIndex((ch) => ch.id === currentChapter.id);
  }, [chapters, currentChapter, hasChapters]);

  const audioUrl = `https://feeds.kaster.media/${currentEpisode.audioS3Key}`;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleEnded = () => onEpisodeEnded();

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);

    // Set source and auto-play when episode changes
    audio.src = audioUrl;
    audio.play().catch(() => {
      // Autoplay blocked, user needs to click play
      setIsPlaying(false);
    });

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioUrl, setIsPlaying, onEpisodeEnded]);

  // Sync playback speed to audio element
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  // Sync isPlaying state with audio element
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying && audio.paused) {
      audio.play().catch(() => setIsPlaying(false));
    } else if (!isPlaying && !audio.paused) {
      audio.pause();
    }
  }, [isPlaying, setIsPlaying]);

  // Periodic progress sync during playback (every 15 seconds)
  useEffect(() => {
    if (!isPlaying || !isAuthenticated) return;

    const intervalId = setInterval(() => {
      const audio = audioRef.current;
      if (audio && audio.duration > 0) {
        syncListenProgress(audio.currentTime, audio.duration);
      }
    }, 15000);

    return () => clearInterval(intervalId);
  }, [isPlaying, isAuthenticated, syncListenProgress]);

  // Sync progress when episode ends
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !isAuthenticated) return;

    const handleEnded = () => {
      // Force sync final progress when episode ends
      if (audio.duration > 0) {
        syncListenProgress(audio.duration, audio.duration, true);
      }
    };

    audio.addEventListener('ended', handleEnded);
    return () => audio.removeEventListener('ended', handleEnded);
  }, [isAuthenticated, syncListenProgress]);

  // Close speed menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (speedMenuRef.current && !speedMenuRef.current.contains(event.target as Node)) {
        setShowSpeedMenu(false);
      }
    };

    if (showSpeedMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSpeedMenu]);

  // Handle swipe gestures for mobile
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!hasChapters) return;
    setIsDragging(true);
    setDragStartY(e.touches[0].clientY);
  }, [hasChapters]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || !hasChapters) return;

    const currentY = e.touches[0].clientY;
    const diff = dragStartY - currentY;

    // Threshold for triggering expand/collapse
    if (Math.abs(diff) > 50) {
      if (diff > 0 && !isExpanded) {
        // Swiping up - expand
        setIsExpanded(true);
        setIsDragging(false);
      } else if (diff < 0 && isExpanded) {
        // Swiping down - collapse
        setIsExpanded(false);
        setIsDragging(false);
      }
    }
  }, [isDragging, dragStartY, isExpanded, hasChapters]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = Number(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const skip = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = Math.max(0, Math.min(audio.currentTime + seconds, duration));
  };

  const seekToChapter = (startTimeMs: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = startTimeMs / 1000;
    setCurrentTime(startTimeMs / 1000);
    if (!isPlaying) {
      setIsPlaying(true);
    }
  };

  const skipToPreviousChapter = () => {
    if (!hasChapters || currentChapterIndex < 0) return;

    const currentTimeMs = currentTime * 1000;
    const currentChapterStartMs = chapters[currentChapterIndex].startTimeMs;

    // If more than 3 seconds into the chapter, restart it
    // Otherwise, go to the previous chapter
    if (currentTimeMs - currentChapterStartMs > 3000 || currentChapterIndex === 0) {
      seekToChapter(currentChapterStartMs);
    } else {
      seekToChapter(chapters[currentChapterIndex - 1].startTimeMs);
    }
  };

  const skipToNextChapter = () => {
    if (!hasChapters || currentChapterIndex < 0) return;

    // Go to the next chapter if available
    if (currentChapterIndex < chapters.length - 1) {
      seekToChapter(chapters[currentChapterIndex + 1].startTimeMs);
    }
  };

  const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  const selectPlaybackSpeed = (speed: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.playbackRate = speed;
    setPlaybackSpeed(speed);
    localStorage.setItem('playbackSpeed', String(speed));
    setShowSpeedMenu(false);
  };

  const toggleExpanded = () => {
    if (hasChapters) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div
      ref={panelRef}
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 transition-all duration-300 ease-out"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <audio ref={audioRef} preload="metadata">
        <track kind="captions" />
      </audio>

      {/* Drag handle - only show when chapters are available */}
      {hasChapters && (
        <button
          ref={dragHandleRef}
          type="button"
          onClick={toggleExpanded}
          className="flex w-full justify-center py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800"
          aria-label={isExpanded ? 'Collapse chapters' : 'Expand chapters'}
          aria-expanded={isExpanded}
        >
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
        </button>
      )}

      {/* Chapters panel - slides in from above */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-out ${
          isExpanded ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {hasChapters && (
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="mx-auto max-w-4xl px-4 pb-3">
              {/* Chapter header */}
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Chapters ({chapters.length})
                </h3>
                {currentChapter && (
                  <span className="text-xs text-sky-600 dark:text-sky-400">
                    Now: {currentChapter.title}
                  </span>
                )}
              </div>

              {/* Scrollable chapter list */}
              <div className="max-h-44 overflow-y-auto">
                <ul className="space-y-1">
                  {chapters.map((chapter, index) => {
                    const isActive = currentChapter?.id === chapter.id;
                    return (
                      <li key={chapter.id}>
                        <button
                          type="button"
                          onClick={() => seekToChapter(chapter.startTimeMs)}
                          className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                            isActive
                              ? 'bg-sky-100 dark:bg-sky-900/50'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-6 text-xs font-medium text-gray-400 dark:text-gray-500 pt-0.5">
                              {index + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${
                                isActive
                                  ? 'text-sky-700 dark:text-sky-300'
                                  : 'text-gray-900 dark:text-gray-100'
                              }`}>
                                {chapter.title}
                              </p>
                              {chapter.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                                  {chapter.description}
                                </p>
                              )}
                            </div>
                            <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500 pt-0.5">
                              {formatTime(chapter.startTimeMs / 1000)}
                            </span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Player controls - stacked layout on mobile for more title space */}
      <div className="mx-auto max-w-4xl px-4 py-3">
        {/* Title row - full width */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 line-clamp-2 text-sm leading-tight">
              {currentEpisode.title}
            </h4>
            <p className="truncate text-xs text-gray-600 dark:text-gray-400 mt-0.5">{currentEpisode.topicName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 rounded-full p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
            title="Close player"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Controls row */}
        <div className="flex items-center justify-center gap-2">
          {hasChapters && (
            <button
              type="button"
              onClick={skipToPreviousChapter}
              className="rounded-full p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
              title="Previous chapter"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6h2v12H6V6zm3.5 6l8.5 6V6l-8.5 6z" />
              </svg>
            </button>
          )}

          <button
            type="button"
            onClick={() => skip(-15)}
            className="rounded-full p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
            title="Rewind 15 seconds"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
              <text x="9" y="15" fontSize="7" fill="currentColor">15</text>
            </svg>
          </button>

          <button
            type="button"
            onClick={togglePlayPause}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-600 text-white hover:bg-sky-700 dark:bg-sky-500 dark:hover:bg-sky-600"
          >
            {isPlaying ? (
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          <button
            type="button"
            onClick={() => skip(15)}
            className="rounded-full p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
            title="Forward 15 seconds"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z" />
              <text x="9" y="15" fontSize="7" fill="currentColor">15</text>
            </svg>
          </button>

          {hasChapters && (
            <button
              type="button"
              onClick={skipToNextChapter}
              className={`rounded-full p-2 ${
                currentChapterIndex < chapters.length - 1
                  ? 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                  : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
              }`}
              title="Next chapter"
              disabled={currentChapterIndex >= chapters.length - 1}
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
              </svg>
            </button>
          )}

          <div className="relative" ref={speedMenuRef}>
            <button
              type="button"
              onClick={() => setShowSpeedMenu(!showSpeedMenu)}
              className="min-w-[2.5rem] rounded-full px-2 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
              title="Playback speed"
            >
              {playbackSpeed}x
            </button>
            {showSpeedMenu && (
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-700">
                {PLAYBACK_SPEEDS.map((speed) => (
                  <button
                    key={speed}
                    type="button"
                    onClick={() => selectPlaybackSpeed(speed)}
                    className={`block w-full px-4 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 ${
                      speed === playbackSpeed
                        ? 'font-medium text-sky-600 dark:text-sky-400'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {speed}x
                  </button>
                ))}
              </div>
            )}
          </div>

          <ShareButton
            topicSlug={currentEpisode.topicSlug}
            topicName={currentEpisode.topicName}
            episodeId={currentEpisode.episodeId}
            episodeTitle={currentEpisode.title}
            variant="icon"
          />

          {hasChapters && (
            <button
              type="button"
              onClick={toggleExpanded}
              className={`rounded-full p-2 transition-colors ${
                isExpanded
                  ? 'bg-sky-100 text-sky-600 dark:bg-sky-900/50 dark:text-sky-400'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
              }`}
              title={isExpanded ? 'Hide chapters' : 'Show chapters'}
              aria-expanded={isExpanded}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className="mt-3 flex items-center gap-3">
          <span className="w-10 text-xs text-gray-500 dark:text-gray-400">
            {formatTime(currentTime)}
          </span>
          <input
            type="range"
            min={0}
            max={duration || 100}
            value={currentTime}
            onChange={handleSeek}
            className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-gray-200 dark:bg-gray-700"
          />
          <span className="w-10 text-right text-xs text-gray-500 dark:text-gray-400">
            {formatTime(duration)}
          </span>
        </div>
      </div>
    </div>
  );
}

function formatTime(seconds: number): string {
  if (!seconds || !Number.isFinite(seconds)) return '0:00';

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
